// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

const SECTIONS_QUERY_KEY = ['custom-sections'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

export function useCustomSections() {
  return useQuery({
    queryKey: SECTIONS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/custom-sections');
      if (error) throw new Error(error.error.message);
      return data.data;
    },
  });
}

export function useCreateCustomSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { title: string; is_visible?: boolean }) => {
      const { data, error } = await apiClient.POST('/api/v1/custom-sections', {
        body: { title: params.title, is_visible: params.is_visible },
      });
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SECTIONS_QUERY_KEY });
    },
  });
}

export function useUpdateCustomSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...params
    }: {
      id: string;
      title?: string;
      is_visible?: boolean;
    }) => {
      const { data, error } = await apiClient.PUT('/api/v1/custom-sections/{id}', {
        params: { path: { id } },
        body: { title: params.title, is_visible: params.is_visible },
      });
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SECTIONS_QUERY_KEY });
    },
  });
}

export function useDeleteCustomSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE('/api/v1/custom-sections/{id}', {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SECTIONS_QUERY_KEY });
    },
  });
}

export function useBulkDeleteCustomSections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST('/api/v1/custom-sections/bulk-delete', {
        body: { ids },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SECTIONS_QUERY_KEY });
    },
  });
}

// --- Entries ---

export function entryQueryKey(sectionId: string) {
  return ['custom-sections', sectionId, 'entries'] as const;
}

export function useCustomSectionEntries(sectionId: string | undefined) {
  return useQuery({
    queryKey: entryQueryKey(sectionId ?? ''),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/api/v1/custom-sections/{sectionId}/entries',
        { params: { path: { sectionId: sectionId! } } },
      );
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    enabled: !!sectionId,
  });
}

export function useCreateCustomSectionEntry(sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      title?: string;
      subtitle?: string;
      description?: string;
      start_date?: string;
      end_date?: string;
      url?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/custom-sections/{sectionId}/entries',
        {
          params: { path: { sectionId } },
          body: {
            title: params.title || undefined,
            subtitle: params.subtitle || undefined,
            description: params.description || undefined,
            start_date: params.start_date || undefined,
            end_date: params.end_date || undefined,
            url: params.url || undefined,
          },
        },
      );
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entryQueryKey(sectionId) });
    },
  });
}

export function useUpdateCustomSectionEntry(sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...params
    }: {
      id: string;
      title?: string;
      subtitle?: string;
      description?: string;
      start_date?: string;
      end_date?: string;
      url?: string;
    }) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/custom-sections/{sectionId}/entries/{entryId}',
        {
          params: { path: { sectionId, entryId: id } },
          body: {
            title: params.title || undefined,
            subtitle: params.subtitle || undefined,
            description: params.description || undefined,
            start_date: params.start_date || undefined,
            end_date: params.end_date || undefined,
            url: params.url || undefined,
          },
        },
      );
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entryQueryKey(sectionId) });
    },
  });
}

export function useDeleteCustomSectionEntry(sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await apiClient.DELETE(
        '/api/v1/custom-sections/{sectionId}/entries/{entryId}',
        { params: { path: { sectionId, entryId } } },
      );
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entryQueryKey(sectionId) });
    },
  });
}

export function useBulkDeleteCustomSectionEntries(sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/custom-sections/{sectionId}/entries/bulk-delete',
        {
          params: { path: { sectionId } },
          body: { ids },
        },
      );
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entryQueryKey(sectionId) });
    },
  });
}

export function useGrammarCheckEntry(sectionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entryId,
      description,
    }: {
      entryId: string;
      description?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/custom-sections/{sectionId}/entries/{entryId}/grammar-check',
        {
          params: { path: { sectionId, entryId } },
          body: description ? { description } : {},
        },
      );
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}

/**
 * AI-polish (or draft) an entry description. Pass `entryId` alone to polish
 * the saved entry; pass inline fields to polish what is currently in the form.
 */
export function usePolishEntry(sectionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entryId,
      title,
      subtitle,
      description,
    }: {
      entryId?: string;
      title?: string;
      subtitle?: string;
      description?: string;
    }) => {
      const pathId = entryId || '00000000-0000-0000-0000-000000000000';
      const inline =
        title !== undefined || subtitle !== undefined || description !== undefined;
      const { data, error } = await apiClient.POST(
        '/api/v1/custom-sections/{sectionId}/entries/{entryId}/polish',
        {
          params: { path: { sectionId, entryId: pathId } },
          body: inline
            ? {
                title: title ?? '',
                subtitle: subtitle ?? '',
                description: description ?? '',
              }
            : {},
        },
      );
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}
